/**
 * Authentication hook
 */

import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { authApi } from '../api/auth';
import type { User, LoginRequest, RegisterRequest } from '../api/types';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  register: (userData: RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // ✅ ENHANCED DEBUG: Log initial state
    const storedUser = authApi.getStoredUser();
    const token = localStorage.getItem('access_token');
    
    console.log('🔍 useAuth - Initial Check:', {
      hasStoredUser: !!storedUser,
      hasToken: !!token,
      storedUser,
      tokenPreview: token ? `${token.substring(0, 20)}...` : null
    });
    
    if (storedUser && token) {
      // Set user immediately from storage (for faster initial render)
      setUser(storedUser);
      console.log('✅ useAuth - User loaded from localStorage:', storedUser);
      
      // ✅ CRITICAL: Always fetch fresh user data to ensure we have role-specific IDs (patient_id, doctor_id, etc.)
      authApi.getCurrentUser()
        .then(currentUser => {
          console.log('✅ useAuth - Fresh user data fetched from /me:', currentUser);
          setUser(currentUser);
          // Update localStorage with fresh data including role-specific IDs
          localStorage.setItem('user', JSON.stringify(currentUser));
        })
        .catch((error) => {
          console.error('❌ useAuth - Failed to fetch current user from /me:', error);
          console.error('❌ useAuth - Error details:', {
            message: error.message,
            status: error.response?.status,
            statusText: error.response?.statusText
          });
          
          // ✅ FIX: Only clear auth if it's a 401/403 (unauthorized/forbidden)
          // Keep cached user for network errors or 5xx errors
          if (error.response?.status === 401 || error.response?.status === 403) {
            console.warn('⚠️ useAuth - Token expired/invalid. Clearing auth data.');
            authApi.logout();
            setUser(null);
          } else {
            console.warn('⚠️ useAuth - /me call failed, but keeping cached user data');
            // Keep the storedUser we already set
          }
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      console.warn('⚠️ useAuth - No stored user or token found. User needs to login.');
      console.log('📊 useAuth - Storage state:', {
        localStorage_keys: Object.keys(localStorage),
        access_token: localStorage.getItem('access_token'),
        user: localStorage.getItem('user')
      });
      setLoading(false);
    }
  }, []);

  const login = async (credentials: LoginRequest) => {
    const response = await authApi.login(credentials);
    setUser(response.user);
  };

  const register = async (userData: RegisterRequest) => {
    const response = await authApi.register(userData);
    setUser(response.user);
  };

  const logout = async () => {
    await authApi.logout();
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        register,
        logout,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}